import { useCallback, useState } from 'react';

import type { EmbedType } from '../DonationFormEmbedModal';

const INLINE_SNIPPETS = ['inline-div'] as const;
const POPUP_SNIPPETS = ['popup-link'] as const;

interface IUseEmbedFormReturn {
  hasAllSnippetsCopied: (embedType: EmbedType) => boolean;
  handleCodeCopied: (snippetId: string) => void;
  resetState: () => void;
}

/**
 * Tracks which embed snippets have been copied so the modal's "Done" button
 * can stay disabled until the user has copied what they need.
 */
export function useEmbedForm(): IUseEmbedFormReturn {
  const [copiedSnippets, setCopiedSnippets] = useState<Set<string>>(new Set());

  function handleCodeCopied(snippetId: string): void {
    setCopiedSnippets((prev) => new Set(prev).add(snippetId));
  }

  function hasAllSnippetsCopied(embedType: EmbedType): boolean {
    if (!embedType) {
      return false;
    }

    const requiredSnippets = embedType === 'inline' ? INLINE_SNIPPETS : POPUP_SNIPPETS;
    return requiredSnippets.every((snippetId) => copiedSnippets.has(snippetId));
  }

  const resetState = useCallback((): void => {
    setCopiedSnippets(new Set());
  }, []);

  return {
    hasAllSnippetsCopied,
    handleCodeCopied,
    resetState,
  };
}
